import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet } from "../lib/api";
import { apiPut } from "./api-ecrire";
import {
  demandeMinimumSchema,
  type ReferenceVue,
  reponseReserveSchema,
  type VueBobines,
  type VueCatalogue,
  vueBobinesSchema,
  vueCatalogueSchema,
} from "./contract";
import { statutDe } from "./queries";

/**
 * Les lectures et les ecritures du module Bobines.
 *
 * Les lectures ne reessayent pas, comme les autres de la console : un 401 ou un 403 est une
 * reponse, pas une panne. La vue ne se relit pas non plus d'elle-meme : l'agent du PC n'envoie
 * qu'une copie par heure, et la couche du Lab compte chaque GET comme une visite, justement la
 * mesure du module (spec, section 5). Les ecritures passent par `apiPut` (`api-ecrire.ts`).
 */
const commun = { retry: false, refetchOnWindowFocus: false } as const;

export const CLE_BOBINES = ["bobines"] as const;
/**
 * Pas sous `CLE_BOBINES` : chaque reglage relit la vue par `invalidateQueries`, qui prend tout
 * ce qui commence par la meme cle, et relirait le catalogue avec elle.
 */
export const CLE_CATALOGUE = ["bobines-catalogue"] as const;

export function useBobines(): UseQueryResult<VueBobines, Error> {
  return useQuery({
    queryKey: CLE_BOBINES,
    queryFn: () => apiGet("/api/bobines", vueBobinesSchema),
    ...commun,
  });
}

/**
 * `GET /api/bobines/catalogue`, lu seulement quand le choix « Ajouter une couleur en reserve »
 * est ouvert : le composant qui s'en sert n'est monte qu'a ce moment-la.
 */
export function useCatalogue(): UseQueryResult<VueCatalogue, Error> {
  return useQuery({
    queryKey: CLE_CATALOGUE,
    queryFn: () => apiGet("/api/bobines/catalogue", vueCatalogueSchema),
    ...commun,
  });
}

/** Les deux reglages de Nicolas sur une couleur. */
export type Reglage = "minimum" | "reserve";

/** Un reglage rendu par la console : la couleur et sa valeur enregistree. */
export type ReglageRendu = { readonly cle: string; readonly valeur: number };

/**
 * Un reglage envoye : la couleur, sa nouvelle valeur, et celle que la page affichait. La
 * reserve n'est ecrite que si la base vaut encore `avant` (409 `reserve_changee` sinon) : une
 * copie a pu la faire baisser dans le dos de la page. Le minimum, que rien ne change dans le dos
 * de la page, garde son ecriture absolue et n'envoie pas `avant`.
 */
export type DemandeReglage = ReglageRendu & { readonly avant: number };

/** Le rachat d'une reference : son minimum moins ses neuves, celles de l'AMS et de la reserve. */
function aRacheter(reference: Pick<ReferenceVue, "minimum" | "neuves" | "reserve">): number {
  return Math.max(0, reference.minimum - (reference.neuves + reference.reserve));
}

/**
 * Pose le reglage enregistre dans la vue en cache, avant la relecture : la ligne garde sa
 * nouvelle valeur meme si la relecture echoue. Le reste (liste de courses, total, une couleur
 * du catalogue qui n'avait pas encore de ligne) vient de la relecture, calcule par le backend.
 */
export function poserReglage(
  vue: VueBobines,
  quoi: Reglage,
  { cle, valeur }: ReglageRendu,
): VueBobines {
  return {
    ...vue,
    gammes: vue.gammes.map((gamme) => ({
      ...gamme,
      references: gamme.references.map((reference) => {
        if (reference.cle !== cle) return reference;
        const reglee: ReferenceVue =
          quoi === "minimum"
            ? { ...reference, minimum: valeur }
            : { ...reference, reserve: valeur };
        return { ...reglee, a_racheter: aRacheter(reglee) };
      }),
    })),
  };
}

/**
 * L'envoi du reglage : `PUT /api/bobines/minimum` (absolu) ou `PUT /api/bobines/reserve`
 * (conditionnel, avec `avant`).
 */
async function envoyer(
  quoi: Reglage,
  { cle, valeur, avant }: DemandeReglage,
): Promise<ReglageRendu> {
  if (quoi === "minimum") {
    const rendu = await apiPut(
      "/api/bobines/minimum",
      { cle, minimum: valeur },
      demandeMinimumSchema,
    );
    return { cle: rendu.cle, valeur: rendu.minimum };
  }
  const rendu = await apiPut(
    "/api/bobines/reserve",
    { cle, reserve: valeur, avant },
    reponseReserveSchema,
  );
  return { cle: rendu.cle, valeur: rendu.reserve };
}

/**
 * Un reglage de Nicolas, minimum ou reserve. Au succes, la vue se relit, et l'envoi reste « en
 * cours » jusqu'a la fin de cette relecture (`onSuccess` rend sa promesse) : les boutons se
 * rallument sur la valeur relue. Une reserve qui avait change dans le dos de la page (409) relit
 * aussi la vue, et l'envoi reste « en cours » jusqu'a la fin de cette relecture (`onError` rend
 * sa promesse, que TanStack Query attend) : le refus s'affiche avec la valeur a jour, et le clic
 * suivant part d'elle. Une session perdue (401) ou une adresse refusee (403) relit aussi la vue,
 * dont l'echec remplace la page par l'ecran qui l'explique.
 */
export function useRegler(quoi: Reglage): UseMutationResult<ReglageRendu, Error, DemandeReglage> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (demande: DemandeReglage) => envoyer(quoi, demande),
    onSuccess: (reponse) => {
      client.setQueryData<VueBobines>(CLE_BOBINES, (vue) =>
        vue === undefined ? vue : poserReglage(vue, quoi, reponse),
      );
      return client.invalidateQueries({ queryKey: CLE_BOBINES });
    },
    onError: (erreur) => {
      const statut = statutDe(erreur);
      if (statut === 409) return client.invalidateQueries({ queryKey: CLE_BOBINES });
      if (statut === 401 || statut === 403) {
        void client.invalidateQueries({ queryKey: CLE_BOBINES });
      }
      return undefined;
    },
  });
}
